import { useCallback, useMemo, useState } from "react";

const EMPTY_CELL_SET = new Set();

export const GRID_SELECTION_CONTROLLER = Symbol("GridCore.selectionController");

const EMPTY_SELECTION = Object.freeze({
    type: "none",
    cells: EMPTY_CELL_SET,
    cellKeys: EMPTY_CELL_SET,
    focus: null,
    focusCell: null,
    rangeStart: null,
});

export function getGridCellKey(rowIdx, colIdx) {
    return `${rowIdx}:${colIdx}`;
}

export function isGridCellSelected(selection, rowIdx, colIdx) {
    if (!selection) return false;

    const cellKey = getGridCellKey(rowIdx, colIdx);
    if (selection.cellKeys instanceof Set) return selection.cellKeys.has(cellKey);

    if (selection.cells instanceof Set) {
        for (const cell of selection.cells) {
            if (cell?.rowIdx === rowIdx && cell?.colIdx === colIdx) return true;
        }
    }

    if (Array.isArray(selection.cells)) {
        return selection.cells.some((cell) => cell?.rowIdx === rowIdx && cell?.colIdx === colIdx);
    }

    return false;
}

function isValidCellPosition(rowIdx, colIdx) {
    return Number.isInteger(rowIdx) && rowIdx >= 0 && Number.isInteger(colIdx) && colIdx >= 0;
}

function normalizeRows(rows) {
    return Array.isArray(rows) ? rows : [];
}

function normalizeColumns(columns) {
    return Array.isArray(columns) ? columns : [];
}

function isCellSelectableColumn(column) {
    return column?.isRowHeader !== true && column?.excludeFromSelection !== true;
}

function canSelectColumn(column) {
    return isCellSelectableColumn(column) && column?.frozen !== true;
}

function getSelectionCells(selection) {
    if (selection?.cells instanceof Set) return Array.from(selection.cells);
    if (Array.isArray(selection?.cells)) return selection.cells;
    return [];
}

function buildCellCollection(cells) {
    const cellsByKey = new Map();

    cells.forEach((cell) => {
        if (!isValidCellPosition(cell?.rowIdx, cell?.colIdx)) return;
        const cellKey = getGridCellKey(cell.rowIdx, cell.colIdx);
        if (!cellsByKey.has(cellKey)) cellsByKey.set(cellKey, cell);
    });

    return {
        cells: new Set(cellsByKey.values()),
        cellKeys: new Set(cellsByKey.keys()),
    };
}

function buildSelection(type, cells, focus, rangeStart) {
    const cellCollection = buildCellCollection(cells);
    if (cellCollection.cells.size === 0) return EMPTY_SELECTION;

    const nextFocus = focus ?? cellCollection.cells.values().next().value ?? null;
    return {
        type,
        ...cellCollection,
        focus: nextFocus,
        focusCell: nextFocus,
        rangeStart: rangeStart ?? nextFocus,
    };
}

export function useGridSelection({ selection: controlledSelection, onSelectionChange, rows, columns, getRows, getColumns, rowCount, getCellMetadata } = {}) {
    const [internalSelection, setInternalSelection] = useState(EMPTY_SELECTION);
    const selection = controlledSelection ?? internalSelection;

    const readRows = useCallback(() => normalizeRows(typeof getRows === "function" ? getRows() : rows), [getRows, rows]);
    const readColumns = useCallback(() => normalizeColumns(typeof getColumns === "function" ? getColumns() : columns), [columns, getColumns]);
    const readRowCount = useCallback(() => {
        if (Number.isInteger(rowCount) && rowCount >= 0) return rowCount;
        return readRows().length;
    }, [readRows, rowCount]);

    const commitSelection = useCallback(
        (nextSelection) => {
            if (controlledSelection === undefined) {
                setInternalSelection(nextSelection);
            }
            onSelectionChange?.(nextSelection);
        },
        [controlledSelection, onSelectionChange],
    );

    const resolveCell = useCallback(
        (rowIdx, colIdx, metadata = {}) => {
            if (!isValidCellPosition(rowIdx, colIdx)) return null;

            const resolvedMetadata = typeof getCellMetadata === "function" ? getCellMetadata(rowIdx, colIdx) || {} : {};
            return {
                rowIdx,
                colIdx,
                ...resolvedMetadata,
                ...metadata,
            };
        },
        [getCellMetadata],
    );

    const isCellSelectable = useCallback(
        (colIdx) => {
            const nextColumns = readColumns();
            if (nextColumns.length === 0) return true;
            return isCellSelectableColumn(nextColumns[colIdx]);
        },
        [readColumns],
    );

    const buildCellsInRange = useCallback(
        (start, end) => {
            const rowStart = Math.min(start.rowIdx, end.rowIdx);
            const rowEnd = Math.max(start.rowIdx, end.rowIdx);
            const colStart = Math.min(start.colIdx, end.colIdx);
            const colEnd = Math.max(start.colIdx, end.colIdx);
            const nextCells = [];

            for (let rowIdx = rowStart; rowIdx <= rowEnd; rowIdx += 1) {
                for (let colIdx = colStart; colIdx <= colEnd; colIdx += 1) {
                    if (!isCellSelectable(colIdx)) continue;
                    const cell = resolveCell(rowIdx, colIdx);
                    if (cell) nextCells.push(cell);
                }
            }

            return nextCells;
        },
        [isCellSelectable, resolveCell],
    );

    const setFocus = useCallback(
        (rowIdx, colIdx, metadata = {}) => {
            if (!isCellSelectable(colIdx)) return;

            const focus = resolveCell(rowIdx, colIdx, metadata);
            if (!focus) return;

            commitSelection(buildSelection("single", [focus], focus, focus));
        },
        [commitSelection, isCellSelectable, resolveCell],
    );

    const extendRange = useCallback(
        (rowIdx, colIdx, metadata = {}) => {
            if (!isCellSelectable(colIdx)) return;

            const focus = resolveCell(rowIdx, colIdx, metadata);
            if (!focus) return;

            const rangeStart = selection.rangeStart ?? selection.focus ?? focus;
            commitSelection(buildSelection("range", buildCellsInRange(rangeStart, focus), focus, rangeStart));
        },
        [buildCellsInRange, commitSelection, isCellSelectable, resolveCell, selection.focus, selection.rangeStart],
    );

    const selectRange = useCallback(
        (startRowIdx, startColIdx, endRowIdx, endColIdx, metadata = {}) => {
            if (!isCellSelectable(startColIdx) || !isCellSelectable(endColIdx)) return;

            const rangeStart = resolveCell(startRowIdx, startColIdx);
            const focus = resolveCell(endRowIdx, endColIdx, metadata);
            if (!rangeStart || !focus) return;

            commitSelection(buildSelection("range", buildCellsInRange(rangeStart, focus), focus, rangeStart));
        },
        [buildCellsInRange, commitSelection, isCellSelectable, resolveCell],
    );

    const toggleCell = useCallback(
        (rowIdx, colIdx, metadata = {}) => {
            if (!isCellSelectable(colIdx)) return;

            const cell = resolveCell(rowIdx, colIdx, metadata);
            if (!cell) return;

            const cellKey = getGridCellKey(rowIdx, colIdx);
            const currentCells = getSelectionCells(selection);
            const hasCell = currentCells.some((currentCell) => getGridCellKey(currentCell.rowIdx, currentCell.colIdx) === cellKey);
            const nextCells = hasCell
                ? currentCells.filter((currentCell) => getGridCellKey(currentCell.rowIdx, currentCell.colIdx) !== cellKey)
                : [...currentCells, cell];

            if (nextCells.length === 0) {
                commitSelection(EMPTY_SELECTION);
                return;
            }

            const focus = hasCell
                ? nextCells.find((nextCell) => selection.focus && getGridCellKey(nextCell.rowIdx, nextCell.colIdx) === getGridCellKey(selection.focus.rowIdx, selection.focus.colIdx)) ?? nextCells[nextCells.length - 1]
                : cell;
            commitSelection(buildSelection("multi", nextCells, focus, selection.rangeStart ?? selection.focus ?? focus));
        },
        [commitSelection, isCellSelectable, resolveCell, selection],
    );

    const selectRow = useCallback(
        (rowIdx) => {
            if (!Number.isInteger(rowIdx) || rowIdx < 0 || rowIdx >= readRowCount()) return;

            const nextColumns = readColumns();
            const nextCells = nextColumns
                .map((column, colIdx) => (isCellSelectableColumn(column) ? resolveCell(rowIdx, colIdx) : null))
                .filter(Boolean);
            const focus = nextCells[0] ?? null;
            if (!focus) return;

            commitSelection(buildSelection("row", nextCells, focus, focus));
        },
        [commitSelection, readColumns, readRowCount, resolveCell],
    );

    const selectColumn = useCallback(
        (colIdx) => {
            const nextColumns = readColumns();
            const column = nextColumns[colIdx];
            if (!canSelectColumn(column)) return;

            const nextCells = [];
            for (let rowIdx = 0; rowIdx < readRowCount(); rowIdx += 1) {
                const cell = resolveCell(rowIdx, colIdx);
                if (cell) nextCells.push(cell);
            }

            const focus = nextCells[0] ?? null;
            if (!focus) return;

            commitSelection(buildSelection("column", nextCells, focus, focus));
        },
        [commitSelection, readColumns, readRowCount, resolveCell],
    );

    const selectBlock = useCallback(
        (blockId) => {
            if (!blockId) return;

            const nextColumns = readColumns();
            const blockColumnIndexes = nextColumns
                .map((column, colIdx) => (column?.blockId === blockId && isCellSelectableColumn(column) ? colIdx : null))
                .filter((colIdx) => colIdx !== null);
            if (blockColumnIndexes.length === 0) return;

            const nextCells = [];
            for (let rowIdx = 0; rowIdx < readRowCount(); rowIdx += 1) {
                blockColumnIndexes.forEach((colIdx) => {
                    const cell = resolveCell(rowIdx, colIdx);
                    if (cell) nextCells.push(cell);
                });
            }

            const focus = nextCells[0] ?? null;
            if (!focus) return;

            commitSelection(buildSelection("block", nextCells, focus, focus));
        },
        [commitSelection, readColumns, readRowCount, resolveCell],
    );

    const clearSelection = useCallback(() => {
        commitSelection(EMPTY_SELECTION);
    }, [commitSelection]);

    const selectionController = useMemo(
        () => ({
            setFocus,
            extendRange,
            selectRange,
            toggleCell,
        }),
        [extendRange, selectRange, setFocus, toggleCell],
    );

    const selectionWithController = useMemo(
        () => ({
            ...selection,
            [GRID_SELECTION_CONTROLLER]: selectionController,
        }),
        [selection, selectionController],
    );

    return {
        selection: selectionWithController,
        setFocus,
        extendRange,
        selectRange,
        toggleCell,
        selectRow,
        selectColumn,
        selectBlock,
        clearSelection,
    };
}
